import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  ShoppingCart,
  Search,
  ExternalLink,
  Loader2,
  AlertCircle,
  CheckCircle,
  Package,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { api, AdminGiftSuggestion, AmazonProduct } from '../lib/api';
import { toast } from 'sonner';

interface AmazonPurchasesPanelProps {
  onNavigate?: (screen: string, data?: any) => void;
}

export function AmazonPurchasesPanel({ }: AmazonPurchasesPanelProps) {
  const [asinInput, setAsinInput] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<AmazonProduct | null>(null);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  
  // Gift suggestions state
  const [suggestions, setSuggestions] = useState<AdminGiftSuggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsPagination, setSuggestionsPagination] = useState({
    total: 0,
    limit: 20,
    offset: 0,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');

  useEffect(() => {
    loadGiftSuggestions();
  }, []);

  const loadGiftSuggestions = async (offset: number = 0, search: string = '', vendor: string = '') => {
    try {
      setSuggestionsLoading(true);
      const response = await api.getAdminGiftSuggestions({
        limit: suggestionsPagination.limit,
        offset,
        search: search || undefined,
        vendor: vendor || undefined,
      });
      setSuggestions(response.suggestions);
      setSuggestionsPagination(response.pagination);
    } catch (error) {
      console.error('Error loading gift suggestions:', error);
      toast.error('Failed to load gift suggestions');
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const handleAsinLookup = async () => {
    if (!asinInput.trim()) {
      toast.error('Please enter an ASIN');
      return;
    }

    try {
      setLookupLoading(true);
      const product = await api.amazonProductLookup(asinInput.trim());
      setSelectedProduct(product);
      setShowProductDialog(true);
      toast.success(`Product found: ${product.name}`);
    } catch (error: any) {
      console.error('Amazon product lookup error:', error);
      toast.error(error.message || 'Failed to lookup product');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleExtractAsinFromUrl = async (url: string) => {
    try {
      const result = await api.amazonExtractAsin(url);
      setAsinInput(result.asin);
      toast.success(`Extracted ASIN: ${result.asin}`);
      // Auto-lookup after extraction
      await handleAsinLookup();
    } catch (error: any) {
      console.error('Failed to extract ASIN:', error);
      toast.error('Could not extract ASIN from URL');
    }
  };

  const handleSuggestionLookup = async (suggestion: AdminGiftSuggestion) => {
    try {
      // Check if URL is from Amazon
      if (suggestion.url.includes('amazon.com') || suggestion.url.includes('amzn.to')) {
        setLookupLoading(true);
        
        // Extract ASIN and lookup
        const result = await api.amazonExtractAsin(suggestion.url);
        const product = await api.amazonProductLookup(result.asin);
        
        setSelectedProduct(product);
        setShowProductDialog(true);
      } else {
        toast.error('This gift is not from Amazon');
      }
    } catch (error: any) {
      console.error('Failed to lookup suggestion:', error);
      toast.error('Failed to lookup product from Amazon');
    } finally {
      setLookupLoading(false);
    }
  };

  const handlePurchase = async () => {
    toast.info('Purchase functionality coming soon!', {
      description: 'This will integrate with Amazon SP-API order placement.',
    });
    // TODO: Integrate with purchase order system
  };

  const handleSearchSuggestions = () => {
    loadGiftSuggestions(0, searchTerm, vendorFilter);
  };

  const handlePrevPage = () => {
    const newOffset = Math.max(0, suggestionsPagination.offset - suggestionsPagination.limit);
    loadGiftSuggestions(newOffset, searchTerm, vendorFilter);
  };

  const handleNextPage = () => {
    const newOffset = suggestionsPagination.offset + suggestionsPagination.limit;
    if (newOffset < suggestionsPagination.total) {
      loadGiftSuggestions(newOffset, searchTerm, vendorFilter);
    }
  };

  const getVendorBadge = (source: string) => {
    const colors: Record<string, string> = {
      amazon: 'bg-orange-100 text-orange-800',
      etsy: 'bg-orange-100 text-orange-800',
      walmart: 'bg-blue-100 text-blue-800',
      target: 'bg-red-100 text-red-800',
      manual: 'bg-gray-100 text-gray-800',
      ai: 'bg-purple-100 text-purple-800',
    };
    
    return (
      <Badge className={colors[source] || 'bg-gray-100 text-gray-800'}>
        {source}
      </Badge>
    );
  };

  const getPurchaseStatusBadge = (status: string) => {
    switch (status) {
      case 'not_ordered':
        return <Badge variant="outline" className="text-gray-600">Not Ordered</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
      case 'ordered':
        return <Badge className="bg-blue-100 text-blue-800">Ordered</Badge>;
      case 'shipped':
        return <Badge className="bg-purple-100 text-purple-800">Shipped</Badge>;
      case 'delivered':
        return <Badge className="bg-green-100 text-green-800">Delivered</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* ASIN Lookup Card */}
      <Card className="p-6">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Package className="w-5 h-5 text-primary" />
          Amazon Product Lookup
        </h3>
        
        <Alert className="mb-4">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>
            Enter an Amazon ASIN (10-character product ID) or paste an Amazon product URL to look up product details.
            <br />
            <span className="text-sm text-muted-foreground">
              Example ASIN: B08N5WRWNW (PlayStation 5) or paste: https://www.amazon.com/dp/B08N5WRWNW
            </span>
          </AlertDescription>
        </Alert>

        <div className="space-y-3">
          <div>
            <Label htmlFor="asin-input">ASIN or Amazon URL</Label>
            <div className="flex gap-2 mt-1">
              <Input
                id="asin-input"
                placeholder="B08N5WRWNW or https://www.amazon.com/..."
                value={asinInput}
                onChange={(e) => setAsinInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (asinInput.includes('amazon.com') || asinInput.includes('amzn.to')) {
                      handleExtractAsinFromUrl(asinInput);
                    } else {
                      handleAsinLookup();
                    }
                  }
                }}
              />
              <Button
                onClick={() => {
                  if (asinInput.includes('amazon.com') || asinInput.includes('amzn.to')) {
                    handleExtractAsinFromUrl(asinInput);
                  } else {
                    handleAsinLookup();
                  }
                }}
                disabled={lookupLoading || !asinInput.trim()}
              >
                {lookupLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Looking up...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Lookup
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Gift Suggestions Table */}
      <Card className="p-6">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <ShoppingCart className="w-5 h-5 text-primary" />
          All Gift Recommendations
        </h3>

        <Alert className="mb-4">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>
            Showing all gift recommendations from all gift groups. In production, only gifts marked "ready to purchase" will appear here.
          </AlertDescription>
        </Alert>

        {/* Search and Filter */}
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by gift name or group title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearchSuggestions()}
              className="pl-10"
            />
          </div>
          <select
            value={vendorFilter}
            onChange={(e) => setVendorFilter(e.target.value)}
            className="border rounded-md px-3 py-2"
          >
            <option value="">All Vendors</option>
            <option value="amazon">Amazon</option>
            <option value="etsy">Etsy</option>
            <option value="walmart">Walmart</option>
            <option value="target">Target</option>
            <option value="manual">Manual</option>
            <option value="ai">AI</option>
          </select>
          <Button onClick={handleSearchSuggestions} variant="outline">
            Search
          </Button>
        </div>

        {/* Suggestions Table */}
        {suggestionsLoading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-2" />
            <p className="text-muted-foreground">Loading gift suggestions...</p>
          </div>
        ) : suggestions.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingCart className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {searchTerm || vendorFilter ? 'No suggestions found matching your filters' : 'No gift suggestions in the system yet'}
            </p>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Gift Name</TableHead>
                  <TableHead>Gift Group</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Suggested By</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suggestions.map((suggestion) => (
                  <TableRow key={suggestion.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {suggestion.imageUrl && (
                          <img
                            src={suggestion.imageUrl}
                            alt={suggestion.name}
                            className="w-10 h-10 object-cover rounded"
                          />
                        )}
                        <div>
                          <p className="font-medium">{suggestion.name}</p>
                          {suggestion.description && (
                            <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                              {suggestion.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{suggestion.giftGroupTitle}</TableCell>
                    <TableCell>${suggestion.price.toFixed(2)}</TableCell>
                    <TableCell>{getVendorBadge(suggestion.source)}</TableCell>
                    <TableCell>{getPurchaseStatusBadge(suggestion.purchaseStatus)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {suggestion.suggestedBy}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {(suggestion.source === 'amazon' || suggestion.url.includes('amazon.com')) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSuggestionLookup(suggestion)}
                            disabled={lookupLoading}
                          >
                            {lookupLoading ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Search className="w-3 h-3" />
                            )}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => window.open(suggestion.url, '_blank')}
                        >
                          <ExternalLink className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">
                Showing {suggestionsPagination.offset + 1} - {Math.min(suggestionsPagination.offset + suggestions.length, suggestionsPagination.total)} of {suggestionsPagination.total} suggestions
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrevPage}
                  disabled={suggestionsPagination.offset === 0}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={suggestionsPagination.offset + suggestions.length >= suggestionsPagination.total}
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* Product Details Dialog */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Amazon Product Details</DialogTitle>
            <DialogDescription>
              Product information from Amazon SP-API
            </DialogDescription>
          </DialogHeader>
          
          {selectedProduct && (
            <div className="space-y-4">
              <div className="flex gap-4">
                {selectedProduct.imageUrl && (
                  <img
                    src={selectedProduct.imageUrl}
                    alt={selectedProduct.name}
                    className="w-32 h-32 object-contain rounded border"
                  />
                )}
                <div className="flex-1 space-y-2">
                  <h4 className="font-semibold text-lg">{selectedProduct.name}</h4>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-orange-100 text-orange-800">
                      ASIN: {selectedProduct.asin}
                    </Badge>
                    {selectedProduct.brand && (
                      <Badge variant="outline">{selectedProduct.brand}</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-primary">
                      ${selectedProduct.price.toFixed(2)}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {selectedProduct.currency}
                    </span>
                  </div>
                  {selectedProduct.availability && (
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm">{selectedProduct.availability}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {selectedProduct.description && (
                <div>
                  <Label>Description</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {selectedProduct.description}
                  </p>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  onClick={() => window.open(selectedProduct.url, '_blank')}
                  variant="outline"
                  className="flex-1"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View on Amazon
                </Button>
                <Button
                  onClick={handlePurchase}
                  className="flex-1"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Purchase (Coming Soon)
                </Button>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProductDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
